/**
 * PACKAGE
 * LOCALIZATION
 * CONSTANTS
 * UTILITY
 * STORE
 * HOC COMPONENTS
 * COMPONENTS
 * ASSETS
 * HELP
 * STYLES
 */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import LazyLoad from 'react-lazyload';
import emoji from 'node-emoji';
// import OndemandVideoIcon from '@material-ui/icons/OndemandVideo';

// CONSTANTS
import { siteUrl } from '@/constants/common';
import { LOCATIONS_HOME_PAGE } from '@/constants/locations';
import {
  ROUTE_DISCOVER,
  ROUTE_START,
  // ROUTE_SHOWS_AND_MOVIES,
  ROUTE_MOBILE_APP,
} from '@/constants/routes';
import { HOME_PAGE } from '@/constants/swimlanes';

// UTILITY
import { analyticsHomepageHeroAccordionClicked, analyticsPageLoaded } from '@/utility/analytics';
import { getOrgSchema } from '@/utility/orgSchemas/orgSchemas';
import { getS3ImageUrl } from '@/utility/content';

// STORE
import {
  AppActions,
  DiscoverActions,
  HomeActions,
  PagesActions,
  SwimlanesActions,
  WPBlogActions,
} from '@/store/actions';

// HOC
import Layout from '@/hoc/Layout';
import withRouter from '@/hoc/withRouter';
import withMediaQuery from '@/hoc/withMediaQuery';

// COMPONENTS
import Reviews from '@/components/Reviews';
import Insights from '@/components/Insights';
import MBIcon from '@/components/Icons/MBIcon';
import Link from '@/components/Link';

// HELP
import Hero from './help/Hero';
import Reasons from './help/Reasons';
import Services from './help/Services';
import Statistic from './help/Statistic';
import WhoWeAre from './help/WhoWeAre';
import CreateFreeProfile from './help/CreateFreeProfile';
import BrowseByService from './help/BrowseByService';
import AboutCoolFeatures from './help/AboutCoolFeatures';
import CollapsibleSection from './help/CollapsibleSection';
import ContentSection from './help/ContentSection';

/* Hide Internet
import FindYourInternet from './help/FindYourInternet';
*/

const defaultOGImg = getS3ImageUrl('/og-image/og-default.png');

const initialCollapse = {
  section1: true,
  section2: true,
  section3: true,
  section4: true,
};

class Home extends PureComponent {
  state = {
    collapse: { ...initialCollapse, section2: false },
  };

  componentDidMount() {
    const { healthCheck, getPagesContent, getHomeNonAuthContent } = this.props;

    healthCheck();
    getPagesContent('homepage,home,home2,share');
    getHomeNonAuthContent('homepage,home,home2,share');
  }

  /*
  //Leaving this because we might bring this back
  componentDidUpdate(prevProps) {
    const { downXs } = this.props;

     if (prevProps.downXs !== downXs && downXs) this.setState({ collapse: initialCollapse });
  }
*/
  componentWillUnmount() {
    const { cleanDiscoverSearch, swimlanesClear } = this.props;

    cleanDiscoverSearch();
    swimlanesClear();
  }

  handleCollapse = section => () => {
    const { collapse } = this.state;

    if (collapse[section]) {
      this.setState(
        {
          collapse: { ...initialCollapse, [section]: !collapse[section] },
        },
        () => {
          switch (section) {
            case 'section2':
              analyticsHomepageHeroAccordionClicked({ item: 'Find My Bundle' });
              break;
            case 'section3':
              analyticsHomepageHeroAccordionClicked({ item: 'Marketplace' });
              break;
            // case 'section4':
            //   analyticsHomepageHeroAccordionClicked({ item: 'Shows and Movies' });
            //   break;
            case 'section4':
              analyticsHomepageHeroAccordionClicked({ item: 'The Mybundle App' });
              break;

            default:
              break;
          }
        },
      );
    }
  };

  areOtherSectionsCollapsed = section => {
    const { collapse } = this.state;
    return !!Object.keys(collapse).find(key => key !== section && !collapse[key]);
  };

  render() {
    const { collapse } = this.state;
    const {
      t,
      content,
      contentLoading,
      router: { location },
      relatedPosts,
      section2,
      section2Loading,
      getWPRelatedPosts,
      cleanWPRelatedPosts,
      partner,
      partnerLoading,
    } = this.props;

    const isPartner = !!partner.slug;
    const isShareReferrer = location.state?.referrer === 'share';
    const seoData = {
      canonicalUrl: siteUrl,
      ogUrl: isPartner ? `${siteUrl}/${partner.slug}` : siteUrl,
      title: isShareReferrer
        ? content['share-og-title']
        : content['meta-home-og-title'] || 'MyBundle – Streaming TV advice for YOUR needs',
      description: isShareReferrer
        ? content['share-og-description']
        : content['meta-home-og-description'] || "Stop wasting money on the TV you don't watch.",
      image: defaultOGImg,
    };

    return (
      <Layout>
        {!partnerLoading && (
          <Helmet
            onChangeClientState={analyticsPageLoaded}
            script={[getOrgSchema('website', seoData)]}
            titleTemplate="%s | MyBundle"
          >
            <title>{content['page-title-home'] || 'Find Your Streaming Service - MyBundle'}</title>
            <meta property="og:title" content={seoData.title} />
            <meta property="og:site_name" content="MyBundle" />
            <link rel="canonical" href={seoData.canonicalUrl} />
            <meta property="og:url" content={seoData.ogUrl} />
            <meta property="og:description" content={seoData.description} />
            <meta name="description" content={seoData.description} />
            <meta property="og:type" content="website" />
            <meta property="fb:app_id" content="608514030003288" />
            <meta property="og:image" content={seoData.image} />
            <meta name="robots" content="index, follow, max-image-preview:large" />
            <meta name="twitter:title" content="MyBundle - Personalized Streaming TV Advice" />
            <meta
              name="twitter:description"
              content="Stop wasting money on the TV you don't watch.  Get your personalized Streaming TV recommendation with MyBundle for free"
            />
            <meta name="twitter:image" content={seoData.image} />
            <meta name="twitter:site" content="@mybundletv" />
            <meta name="twitter:creator" content="@mybundletv" />
            <meta name="twitter:card" content="summary" />
          </Helmet>
        )}
        <Hero />
        <CollapsibleSection
          in={!collapse.section2}
          variant="white"
          onClick={this.handleCollapse('section2')}
          title={t('home.ridCable.collapsedTitle')}
          ButtonProps={{
            children: t('buttons.findMyBundle'),
            endIcon: <MBIcon />,
            color: 'primary',
            location: LOCATIONS_HOME_PAGE,
            to: ROUTE_START,
            component: Link,
          }}
          showShadow={this.areOtherSectionsCollapsed('section2')}
        >
          <ContentSection
            variant="white"
            title={`${t('home.ridCable.title')}\u00A0${emoji.get('tv')}`}
            subtitle={t('home.ridCable.subtitle')}
            description={t('home.ridCable.description')}
            imageName="channels-with-circles"
            ButtonProps={{
              children: t('buttons.findMyBundle'),
              endIcon: <MBIcon />,
              color: 'primary',
              location: LOCATIONS_HOME_PAGE,
              to: ROUTE_START,
              component: Link,
            }}
          />
        </CollapsibleSection>
        <CollapsibleSection
          in={!collapse.section3}
          variant="purple"
          onClick={this.handleCollapse('section3')}
          title={t('home.bestStreamingApps.collapsedTitle')}
          ButtonProps={{
            children: t('buttons.exploreMarketplace'),
            endIcon: <i className="icon-compass" />,
            color: 'secondary',
            location: LOCATIONS_HOME_PAGE,
            to: ROUTE_DISCOVER,
            component: Link,
          }}
          showShadow={this.areOtherSectionsCollapsed('section3')}
        >
          <ContentSection
            variant="purple"
            title={`${t('home.bestStreamingApps.title')}\u00A0${emoji.get('world_map')}`}
            subtitle={t('home.bestStreamingApps.subtitle')}
            description={t('home.bestStreamingApps.description')}
            imageName="channels-with-gradient"
            ButtonProps={{
              children: t('buttons.exploreMarketplace'),
              endIcon: <i className="icon-compass" />,
              color: 'secondary',
              location: LOCATIONS_HOME_PAGE,
              to: ROUTE_DISCOVER,
              component: Link,
            }}
          />
        </CollapsibleSection>
        {/* <CollapsibleSection */}
        {/*  in={!collapse.section4} */}
        {/*  variant="black" */}
        {/*  onClick={this.handleCollapse('section4')} */}
        {/*  title={t('home.findNewShow.collapsedTitle')} */}
        {/*  ButtonProps={{ */}
        {/*    children: t('buttons.findWhatToWatch'), */}
        {/*    endIcon: <OndemandVideoIcon />, */}
        {/*    color: 'primary', */}
        {/*    location: LOCATIONS_HOME_PAGE, */}
        {/*    to: ROUTE_SHOWS_AND_MOVIES, */}
        {/*    component: Link, */}
        {/*  }} */}
        {/*  showShadow={this.areOtherSectionsCollapsed('section4')} */}
        {/* > */}
        {/*  <ContentSection */}
        {/*    variant="black" */}
        {/*    title={`${t('home.findNewShow.title')}\u00A0${emoji.get('film_frames')}`} */}
        {/*    subtitle={t('home.findNewShow.subtitle')} */}
        {/*    description={t('home.findNewShow.description')} */}
        {/*    imageName="content-thumbnails" */}
        {/*    ButtonProps={{ */}
        {/*      children: t('buttons.findWhatToWatch'), */}
        {/*      endIcon: <OndemandVideoIcon />, */}
        {/*      color: 'primary', */}
        {/*      location: LOCATIONS_HOME_PAGE, */}
        {/*      to: ROUTE_SHOWS_AND_MOVIES, */}
        {/*      component: Link, */}
        {/*    }} */}
        {/*  /> */}
        {/* </CollapsibleSection> */}
        <CollapsibleSection
          in={!collapse.section4}
          variant="black"
          onClick={this.handleCollapse('section4')}
          title={t('home.getTheApp.collapsedTitle')}
          ButtonProps={{
            children: t('buttons.getTheApp'),
            endIcon: <i className="icon-square-arrow-right" />,
            color: 'primary',
            location: LOCATIONS_HOME_PAGE,
            to: ROUTE_MOBILE_APP,
            component: Link,
          }}
          showShadow={this.areOtherSectionsCollapsed('section4')}
        >
          <ContentSection
            variant="black"
            title={`${t('home.getTheApp.title')}\u00A0${emoji.get('popcorn')}`}
            subtitle={t('home.getTheApp.subtitle')}
            description={t('home.getTheApp.description')}
            imageName="home-download-app"
            ButtonProps={{
              children: t('buttons.getTheApp'),
              endIcon: <i className="icon-square-arrow-right" />,
              color: 'primary',
              location: LOCATIONS_HOME_PAGE,
              to: ROUTE_MOBILE_APP,
              component: Link,
            }}
          />
        </CollapsibleSection>
        <LazyLoad once className="content-visibility--auto">
          <Reasons content={content} loading={contentLoading} />
          <Services content={content} loading={contentLoading} />
          <AboutCoolFeatures content={content} loading={contentLoading} />
          <CreateFreeProfile />
          <BrowseByService data={section2} loadings={section2Loading} />
          <Reviews />
          <Statistic numberOfApps={content['number-of-apps'] || '140+'} />
          {/* NEW HERE */}
          {/* Hide Internet
          <FindYourInternet />
           */}
          <Insights
            relatedPosts={relatedPosts}
            bg="white"
            showMore
            showSubtitle
            getWPRelatedPosts={getWPRelatedPosts}
            cleanWPRelatedPosts={cleanWPRelatedPosts}
            analyticsLocation={LOCATIONS_HOME_PAGE}
            ButtonProps={{
              color: 'secondary',
              variant: 'contained',
              style: { width: 272 },
            }}
          />
          <WhoWeAre bg={relatedPosts.length ? 'grey' : 'white'} />
          <CreateFreeProfile />
        </LazyLoad>
      </Layout>
    );
  }
}

Home.propTypes = {
  t: PropTypes.func.isRequired,
  downXs: PropTypes.bool.isRequired,
  router: PropTypes.object.isRequired,
  content: PropTypes.object.isRequired,
  contentLoading: PropTypes.bool.isRequired,
  relatedPosts: PropTypes.array.isRequired,
  loadingRelatedPosts: PropTypes.bool.isRequired,
  partner: PropTypes.object.isRequired,
  partnerLoading: PropTypes.bool.isRequired,
  section2: PropTypes.object.isRequired,
  section2Loading: PropTypes.object.isRequired,
  healthCheck: PropTypes.func.isRequired,
  getPagesContent: PropTypes.func.isRequired,
  getHomeNonAuthContent: PropTypes.func.isRequired,
  getWPRelatedPosts: PropTypes.func.isRequired,
  cleanWPRelatedPosts: PropTypes.func.isRequired,
  cleanDiscoverSearch: PropTypes.func.isRequired,
  swimlanesClear: PropTypes.func.isRequired,
};

const mapStateToProps = ({ swimlanes, pages, wpBlog, partner }) => ({
  content: pages.content,
  contentLoading: pages.loading,
  relatedPosts: wpBlog.relatedPosts,
  loadingRelatedPosts: wpBlog.loadings.relatedPosts,
  partner: partner.data,
  partnerLoading: partner.loadings.partner,
  section2: swimlanes.section2[HOME_PAGE] || {},
  section2Loading: swimlanes.loadings.section2[HOME_PAGE] || {},
});

const mapDispatchToProps = dispatch => {
  return {
    healthCheck: () => dispatch(AppActions.healthCheck.action()),
    getPagesContent: pageSlug => dispatch(PagesActions.getPagesContent.action({ pageSlug })),
    getHomeNonAuthContent: () => dispatch(HomeActions.getHomeNonAuthContent.action()),
    getWPRelatedPosts: params => dispatch(WPBlogActions.getWPRelatedPosts.action({ params })),
    cleanWPRelatedPosts: () => dispatch(WPBlogActions.cleanWPRelatedPosts.action()),
    cleanDiscoverSearch: () => dispatch(DiscoverActions.cleanDiscoverSearch.action()),
    swimlanesClear: () => dispatch(SwimlanesActions.swimlanesClear.action()),
  };
};

export default compose(
  withMediaQuery({ downXs: theme => theme.breakpoints.down('xs') }),
  connect(mapStateToProps, mapDispatchToProps),
  withRouter,
  withTranslation(),
  React.memo,
)(Home);
